import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  deleteObject,
  getBlob,
  getDownloadURL,
  getMetadata,
  list,
  ref,
  uploadBytes,
} from "firebase/storage";
import { storage } from "../firebase";
import StoragePreview from "../components/StoragePreview";
import {
  clearStorageExplorerCache,
  getCachedStorageMeta,
  removeCachedStorageMeta,
  removeCachedStorageUrl,
  setCachedStorageMeta,
} from "../utils/storageCache";

const PAGE_SIZE = 30;
const METADATA_CONCURRENCY = 6;

function joinPath(base, name) {
  if (!base) return name;
  return `${base}/${name}`;
}

function getParentPath(path) {
  if (!path) return "";

  const parts = path.split("/").filter(Boolean);
  parts.pop();

  return parts.join("/");
}

function sanitizeFolderName(value) {
  return String(value || "")
    .trim()
    .replace(/^\/+|\/+$/g, "");
}

function formatBytes(bytes = 0) {
  if (!bytes) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );

  const value = bytes / Math.pow(1024, index);

  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

async function runWithConcurrency(items, limit, task) {
  let index = 0;

  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (index < items.length) {
        const currentIndex = index;
        index += 1;
        await task(items[currentIndex]);
      }
    }
  );

  await Promise.all(workers);
}

function buildFastFileData(itemRef) {
  const cachedMeta = getCachedStorageMeta(itemRef.fullPath);

  if (cachedMeta) {
    return {
      name: itemRef.name,
      fullPath: itemRef.fullPath,
      size: cachedMeta.size || 0,
      contentType: cachedMeta.contentType || "",
      updated: cachedMeta.updated || "",
      cacheControl: cachedMeta.cacheControl || "",
      metadataLoading: false,
    };
  }

  return {
    name: itemRef.name,
    fullPath: itemRef.fullPath,
    size: 0,
    contentType: "",
    updated: "",
    cacheControl: "",
    metadataLoading: true,
  };
}

function normalizeMetadata(itemRef, metadata) {
  return {
    name: itemRef.name,
    fullPath: itemRef.fullPath,
    size: metadata.size || 0,
    contentType: metadata.contentType || "",
    updated: metadata.updated || "",
    cacheControl: metadata.cacheControl || "",
    metadataLoading: false,
  };
}

function StorageExplorerPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPath = searchParams.get("path") || "";

  const [currentPath, setCurrentPath] = useState(initialPath);
  const [loadedPath, setLoadedPath] = useState(null);

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);

  const [pageToken, setPageToken] = useState(undefined);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [workingPath, setWorkingPath] = useState("");

  const [previewEnabled, setPreviewEnabled] = useState(false);

  const [newFolderName, setNewFolderName] = useState("");
  const [renameFile, setRenameFile] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const listRequestIdRef = useRef(0);
  const currentPathRef = useRef(initialPath);

  const breadcrumbParts = useMemo(() => {
    const parts = currentPath.split("/").filter(Boolean);
    const result = [{ label: "Root", path: "" }];

    let path = "";

    parts.forEach((part) => {
      path = path ? `${path}/${part}` : part;
      result.push({ label: part, path });
    });

    return result;
  }, [currentPath]);

  const isShowingCurrentPath = loadedPath === currentPath && !loading;

  function resetFolderView(path) {
    currentPathRef.current = path;
    listRequestIdRef.current += 1;

    setLoading(true);
    setLoadingMore(false);
    setFolders([]);
    setFiles([]);
    setPageToken(undefined);
    setHasMore(false);
    setLoadedPath(null);
  }

  function goToPath(path) {
    const cleanPath = path || "";

    if (cleanPath === currentPath) return;

    resetFolderView(cleanPath);
    setCurrentPath(cleanPath);

    if (cleanPath) {
      setSearchParams({ path: cleanPath });
    } else {
      setSearchParams({});
    }
  }

  useEffect(() => {
    const pathFromUrl = searchParams.get("path") || "";

    if (pathFromUrl !== currentPath) {
      resetFolderView(pathFromUrl);
      setCurrentPath(pathFromUrl);
    }
  }, [searchParams, currentPath]);

  const hydrateMetadataInBackground = useCallback(async (path, itemRefs) => {
    const missingRefs = itemRefs.filter(
      (itemRef) => !getCachedStorageMeta(itemRef.fullPath)
    );

    if (missingRefs.length === 0) return;

    try {
      await runWithConcurrency(
        missingRefs,
        METADATA_CONCURRENCY,
        async (itemRef) => {
          if (currentPathRef.current !== path) return;

          try {
            const metadata = await getMetadata(itemRef);
            const fileData = normalizeMetadata(itemRef, metadata);

            setCachedStorageMeta(itemRef.fullPath, fileData);

            if (currentPathRef.current !== path) return;

            setFiles((prevFiles) =>
              prevFiles.map((file) =>
                file.fullPath === itemRef.fullPath
                  ? {
                      ...file,
                      ...fileData,
                    }
                  : file
              )
            );
          } catch {
            if (currentPathRef.current !== path) return;

            setFiles((prevFiles) =>
              prevFiles.map((file) =>
                file.fullPath === itemRef.fullPath
                  ? {
                      ...file,
                      metadataLoading: false,
                    }
                  : file
              )
            );
          }
        }
      );
    } catch (error) {
      console.error("Error cargando metadata en segundo plano:", error);
    }
  }, []);

  const loadFolder = useCallback(
    async (path = "", token = undefined, append = false) => {
      const cleanPath = path || "";
      const requestId = listRequestIdRef.current + 1;

      listRequestIdRef.current = requestId;
      currentPathRef.current = cleanPath;

      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setFolders([]);
          setFiles([]);
          setPageToken(undefined);
          setHasMore(false);
          setLoadedPath(null);
        }

        const folderRef = ref(storage, cleanPath);

        const result = await list(folderRef, {
          maxResults: PAGE_SIZE,
          pageToken: token,
        });

        if (listRequestIdRef.current !== requestId) return;
        if (currentPathRef.current !== cleanPath) return;

        const folderData = result.prefixes.map((prefixRef) => ({
          name: prefixRef.name,
          fullPath: prefixRef.fullPath,
        }));

        const fastFileData = result.items.map(buildFastFileData);

        if (append) {
          setFolders((prevFolders) => {
            const merged = [...prevFolders];
            const existingPaths = new Set(
              prevFolders.map((item) => item.fullPath)
            );

            folderData.forEach((folder) => {
              if (!existingPaths.has(folder.fullPath)) {
                merged.push(folder);
              }
            });

            return merged;
          });

          setFiles((prevFiles) => {
            const merged = [...prevFiles];
            const existingPaths = new Set(
              prevFiles.map((item) => item.fullPath)
            );

            fastFileData.forEach((file) => {
              if (!existingPaths.has(file.fullPath)) {
                merged.push(file);
              }
            });

            return merged;
          });
        } else {
          setFolders(folderData);
          setFiles(fastFileData);
        }

        setPageToken(result.nextPageToken);
        setHasMore(Boolean(result.nextPageToken));
        setLoadedPath(cleanPath);

        hydrateMetadataInBackground(cleanPath, result.items);
      } catch (error) {
        if (listRequestIdRef.current !== requestId) return;

        console.error("Error listando Storage:", error);
        alert("No se pudo cargar esta carpeta de Storage.");
      } finally {
        if (listRequestIdRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [hydrateMetadataInBackground]
  );

  useEffect(() => {
    loadFolder(currentPath, undefined, false);
  }, [currentPath, loadFolder]);

  async function handleRefresh() {
    clearStorageExplorerCache();
    await loadFolder(currentPath, undefined, false);
  }

  async function handleUploadFiles(event) {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);

      await Promise.all(
        selectedFiles.map((file) => {
          const filePath = joinPath(currentPath, file.name);

          return uploadBytes(ref(storage, filePath), file, {
            contentType: file.type || "application/octet-stream",
            cacheControl: "public,max-age=604800",
            customMetadata: {
              uploadedFrom: "dashboard-storage-explorer",
              uploadedAt: String(Date.now()),
            },
          });
        })
      );

      selectedFiles.forEach((file) => {
        const filePath = joinPath(currentPath, file.name);
        removeCachedStorageUrl(filePath);
        removeCachedStorageMeta(filePath);
      });

      event.target.value = "";
      await loadFolder(currentPath, undefined, false);
    } catch (error) {
      console.error("Error subiendo archivos:", error);
      alert("No se pudieron subir los archivos.");
    } finally {
      setUploading(false);
    }
  }

  async function createFolderPlaceholder() {
    const folderName = sanitizeFolderName(newFolderName);

    if (!folderName) {
      alert("Escribe un nombre de carpeta.");
      return;
    }

    try {
      setUploading(true);

      const placeholderPath = joinPath(currentPath, `${folderName}/.keep`);

      await uploadBytes(ref(storage, placeholderPath), new Blob([""]), {
        contentType: "text/plain",
        cacheControl: "public,max-age=3600",
      });

      removeCachedStorageUrl(placeholderPath);
      removeCachedStorageMeta(placeholderPath);

      setNewFolderName("");
      await loadFolder(currentPath, undefined, false);
    } catch (error) {
      console.error("Error creando carpeta:", error);
      alert("No se pudo crear la carpeta.");
    } finally {
      setUploading(false);
    }
  }

  async function deleteFile(file) {
    const confirmed = window.confirm(
      `¿Eliminar este archivo?\n\n${file.fullPath}`
    );

    if (!confirmed) return;

    try {
      setWorkingPath(file.fullPath);

      await deleteObject(ref(storage, file.fullPath));

      removeCachedStorageUrl(file.fullPath);
      removeCachedStorageMeta(file.fullPath);

      await loadFolder(currentPath, undefined, false);
    } catch (error) {
      console.error("Error eliminando archivo:", error);
      alert("No se pudo eliminar el archivo.");
    } finally {
      setWorkingPath("");
    }
  }

  async function openFileInNewTab(file) {
    const tab = window.open("about:blank", "_blank");

    if (!tab) {
      alert("El navegador bloqueó la nueva pestaña.");
      return;
    }

    tab.opener = null;
    tab.document.title = "Cargando archivo...";
    tab.document.body.innerHTML = "<p>Cargando archivo...</p>";

    try {
      setWorkingPath(file.fullPath);

      const fileUrl = await getDownloadURL(ref(storage, file.fullPath));

      tab.location.href = fileUrl;
    } catch (error) {
      console.error("Error abriendo archivo:", error);
      tab.close();
      alert("No se pudo abrir el archivo.");
    } finally {
      setWorkingPath("");
    }
  }

  async function downloadFile(file) {
    try {
      setWorkingPath(file.fullPath);

      const blob = await getBlob(ref(storage, file.fullPath));
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.name || "archivo";
      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      console.error("Error descargando archivo:", error);
      alert("No se pudo descargar el archivo.");
    } finally {
      setWorkingPath("");
    }
  }

  function openRename(file) {
    setRenameFile(file);
    setRenameValue(file.name);
  }

  function closeRename() {
    setRenameFile(null);
    setRenameValue("");
  }

  async function renameCurrentFile(event) {
    event.preventDefault();

    if (!renameFile) return;

    const newName = renameValue.trim();

    if (!newName) {
      alert("Escribe el nuevo nombre.");
      return;
    }

    if (newName.includes("/")) {
      alert("El nombre no debe incluir /. Usa solo el nombre del archivo.");
      return;
    }

    const oldPath = renameFile.fullPath;
    const newPath = joinPath(getParentPath(oldPath), newName);

    if (oldPath === newPath) {
      closeRename();
      return;
    }

    const confirmed = window.confirm(
      `Firebase Storage no renombra directamente.\n\nSe copiará:\n${oldPath}\n\na:\n${newPath}\n\ny luego se eliminará el archivo original.`
    );

    if (!confirmed) return;

    try {
      setWorkingPath(oldPath);

      const oldRef = ref(storage, oldPath);
      const newRef = ref(storage, newPath);

      const blob = await getBlob(oldRef);

      await uploadBytes(newRef, blob, {
        contentType:
          renameFile.contentType || blob.type || "application/octet-stream",
        cacheControl: renameFile.cacheControl || "public,max-age=604800",
        customMetadata: {
          renamedFrom: oldPath,
          renamedAt: String(Date.now()),
        },
      });

      await deleteObject(oldRef);

      removeCachedStorageUrl(oldPath);
      removeCachedStorageMeta(oldPath);
      removeCachedStorageUrl(newPath);
      removeCachedStorageMeta(newPath);

      closeRename();
      await loadFolder(currentPath, undefined, false);
    } catch (error) {
      console.error("Error renombrando archivo:", error);
      alert("No se pudo renombrar el archivo.");
    } finally {
      setWorkingPath("");
    }
  }

  return (
    <>
      <section className="admin-section-card storage-page">
        <div className="section-header storage-header">
          <div>
            <span className="section-eyebrow">Storage</span>
            <h2>Explorador de archivos</h2>
            <p>
              Navega carpetas, previsualiza imágenes, sube, renombra y elimina
              archivos.
            </p>
          </div>

          <div className="section-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={handleRefresh}
              disabled={loading || uploading}
            >
              🔄 Actualizar
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={() => setPreviewEnabled((prev) => !prev)}
            >
              {previewEnabled ? "🌑 Ocultar previews" : "🌕 Cargar previews"}
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                clearStorageExplorerCache();
                alert("Cache local del explorador limpiada.");
              }}
            >
              🧹 Limpiar cache
            </button>

            <label className="primary-action-button storage-upload-button">
              ⬆️ Subir archivo
              <input
                type="file"
                multiple
                onChange={handleUploadFiles}
                disabled={uploading}
              />
            </label>
          </div>
        </div>

        <div className="storage-summary-grid">
          <article className="storage-summary-card">
            <span>📁</span>
            <div>
              <strong>{folders.length}</strong>
              <small>Carpetas cargadas</small>
            </div>
          </article>

          <article className="storage-summary-card">
            <span>📄</span>
            <div>
              <strong>{files.length}</strong>
              <small>Archivos cargados</small>
            </div>
          </article>

          <article className="storage-summary-card">
            <span>{previewEnabled ? "🌕" : "🌑"}</span>
            <div>
              <strong>{previewEnabled ? "On" : "Off"}</strong>
              <small>Previews</small>
            </div>
          </article>
        </div>

        <div className="storage-toolbar">
          <div className="storage-breadcrumb">
            {breadcrumbParts.map((part, index) => (
              <button
                key={part.path || "root"}
                type="button"
                onClick={() => goToPath(part.path)}
                disabled={part.path === currentPath}
              >
                {part.label}
                {index < breadcrumbParts.length - 1 && <span>/</span>}
              </button>
            ))}
          </div>

          <div className="storage-folder-create">
            <input
              type="text"
              placeholder="Nueva carpeta"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />

            <button
              type="button"
              className="secondary-button"
              onClick={createFolderPlaceholder}
              disabled={uploading}
            >
              Crear carpeta
            </button>
          </div>
        </div>

        <div className="storage-current-path">
          <span>Ruta actual</span>
          <code>{currentPath || "/"}</code>
        </div>

        {!isShowingCurrentPath ? (
          <main className="users-loading-state">
            <section className="loading-card">
              <div className="banter-loader" aria-hidden="true">
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
              </div>

              <h2>Cargando Storage...</h2>
              <p>Consultando la ruta {currentPath || "/"}.</p>
            </section>
          </main>
        ) : (
          <>
            <div className="storage-grid">
              {currentPath && (
                <button
                  type="button"
                  className="storage-folder-card storage-folder-card--back"
                  onClick={() => goToPath(getParentPath(currentPath))}
                >
                  <span>↩️</span>
                  <strong>Volver</strong>
                  <small>{getParentPath(currentPath) || "/"}</small>
                </button>
              )}

              {folders.map((folder) => (
                <button
                  key={folder.fullPath}
                  type="button"
                  className="storage-folder-card"
                  onClick={() => goToPath(folder.fullPath)}
                >
                  <span>📁</span>
                  <strong>{folder.name}</strong>
                  <small>{folder.fullPath}</small>
                </button>
              ))}

              {files.map((file) => (
                <article key={file.fullPath} className="storage-file-card">
                  <button
                    type="button"
                    className="storage-preview-open-button"
                    onClick={() => openFileInNewTab(file)}
                    disabled={workingPath === file.fullPath}
                    title="Abrir archivo en otra pestaña"
                    aria-label={`Abrir ${file.name} en otra pestaña`}
                  >
                    <StoragePreview file={file} enabled={previewEnabled} />
                  </button>

<div className="storage-file-info" title={file.fullPath}>
  <strong>{file.name}</strong>

  <small>
    {file.metadataLoading
      ? "Cargando..."
      : `${file.contentType || "Sin tipo"} · ${formatBytes(file.size)}`}
  </small>
</div>

<div className="storage-file-actions">
  <button
    type="button"
    className="small-button storage-action-button"
    onClick={() => downloadFile(file)}
    disabled={Boolean(workingPath)}
    title="Descargar archivo"
    aria-label={`Descargar ${file.name}`}
  >
    <span>Descargar</span>
  </button>

  <button
    type="button"
    className="small-button storage-action-button"
    onClick={() => openRename(file)}
    disabled={Boolean(workingPath)}
    title="Renombrar archivo"
    aria-label={`Renombrar ${file.name}`}
  >
    <span>Renombrar</span>
  </button>

  <button
    type="button"
    className="small-button small-button--danger storage-action-button"
    onClick={() => deleteFile(file)}
    disabled={workingPath === file.fullPath}
    title="Eliminar archivo"
    aria-label={`Eliminar ${file.name}`}
  >
    {workingPath === file.fullPath ? (
      "..."
    ) : (
      <>
        <span>Borrar</span>
      </>
    )}
  </button>
</div>
                </article>
              ))}

              {folders.length === 0 && files.length === 0 && (
                <p className="empty-state storage-empty-state">
                  Esta carpeta está vacía.
                </p>
              )}
            </div>

            {hasMore && (
              <div className="storage-load-more">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => loadFolder(currentPath, pageToken, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Cargando..." : "Cargar más"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {renameFile && (
        <div className="modal-backdrop">
          <div className="edit-modal">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">Storage</span>
                <h3>Renombrar archivo</h3>
                <p>{renameFile.fullPath}</p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeRename}
                disabled={Boolean(workingPath)}
              >
                ×
              </button>
            </div>

            <form className="edit-form" onSubmit={renameCurrentFile}>
              <label>
                Nuevo nombre
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  disabled={Boolean(workingPath)}
                />
                <small className="field-hint">
                  Se copiará el archivo al nuevo nombre y luego se eliminará el
                  original.
                </small>
              </label>

              <div className="path-preview">
                <strong>Vista previa</strong>
                <span>Original: {renameFile.fullPath}</span>
                <span>
                  Nuevo:{" "}
                  {joinPath(
                    getParentPath(renameFile.fullPath),
                    renameValue || renameFile.name
                  )}
                </span>
              </div>

              <div className="edit-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeRename}
                  disabled={Boolean(workingPath)}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="primary-action-button"
                  disabled={Boolean(workingPath)}
                >
                  {workingPath ? "Renombrando..." : "Renombrar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default StorageExplorerPage;